import React from 'react';
import { Link } from 'react-router-dom';
import {
  SUPPORT_EMAIL,
  SUPPORT_PHONE,
  SUPPORT_PHONE_TEL,
  POWERED_BY,
  OFFICE_ADDRESS,
  BUSINESS_HOURS,
  GOOGLE_MAPS_DIRECTIONS_URL,
  GOOGLE_MAPS_EMBED_URL,
} from '../../legal/supportConstants';

const tocItems = [
  { id: 'powered-by', label: 'Powered by' },
  { id: 'contact', label: 'Contact details' },
  { id: 'business-hours', label: 'Business hours' },
  { id: 'office', label: 'Office address' },
  { id: 'map', label: 'Find us on map' },
];

const SupportPage = () => {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      <header className="border-b border-gray-200 dark:border-gray-700 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex flex-wrap items-center justify-between gap-3">
          <span className="text-lg font-semibold text-primary-600 dark:text-primary-400">
            WorkLoop
          </span>
          <nav className="flex flex-wrap items-center gap-4 text-sm">
            <Link
              to="/privacy"
              className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
            >
              Privacy Policy
            </Link>
            <Link
              to="/terms"
              className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
            >
              Terms
            </Link>
            <Link
              to="/app"
              className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
            >
              About
            </Link>
            <Link
              to="/login"
              className="font-medium text-primary-600 hover:text-primary-500 dark:text-primary-400 dark:hover:text-primary-300"
            >
              Sign in
            </Link>
            <Link
              to="/register"
              className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
            >
              Create account
            </Link>
          </nav>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 lg:py-14">
        <div className="lg:grid lg:grid-cols-[minmax(0,14rem)_minmax(0,1fr)] lg:gap-12 xl:gap-16">
          <aside className="hidden lg:block">
            <nav
              className="sticky top-28 space-y-1 text-sm border border-gray-200 dark:border-gray-700 rounded-xl p-4 bg-white dark:bg-gray-800 shadow-sm"
              aria-label="On this page"
            >
              <p className="font-semibold text-gray-900 dark:text-white mb-3 px-2">
                On this page
              </p>
              <ul className="space-y-1">
                {tocItems.map((item) => (
                  <li key={item.id}>
                    <a
                      href={`#${item.id}`}
                      className="block px-2 py-1.5 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                    >
                      {item.label}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </aside>

          <main className="min-w-0 max-w-3xl">
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white tracking-tight">
              Support
            </h1>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              Get in touch with the WorkLoop team
            </p>

            <div className="mt-8 rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 sm:p-8 shadow-sm space-y-10">
              <section id="powered-by" className="scroll-mt-24">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  Powered By {POWERED_BY}
                </h2>
                <p className="text-gray-700 dark:text-gray-300">
                  WorkLoop is powered by {POWERED_BY}. For product support, account help, or
                  technical questions, please use the contact details below.
                </p>
              </section>

              <section
                id="contact"
                className="scroll-mt-24 border-t border-gray-200 dark:border-gray-700 pt-10"
              >
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  Contact Details
                </h2>
                <ul className="space-y-3 not-prose">
                  <li>
                    <span className="text-gray-500 dark:text-gray-400">Email: </span>
                    <a
                      href={`mailto:${SUPPORT_EMAIL}`}
                      className="text-primary-600 dark:text-primary-400 font-medium hover:underline"
                    >
                      {SUPPORT_EMAIL}
                    </a>
                  </li>
                  <li>
                    <span className="text-gray-500 dark:text-gray-400">Contact: </span>
                    <a
                      href={`tel:${SUPPORT_PHONE_TEL}`}
                      className="text-primary-600 dark:text-primary-400 font-medium hover:underline"
                    >
                      {SUPPORT_PHONE}
                    </a>
                  </li>
                </ul>
              </section>

              <section
                id="business-hours"
                className="scroll-mt-24 border-t border-gray-200 dark:border-gray-700 pt-10"
              >
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  Business Hours
                </h2>
                <p className="text-gray-700 dark:text-gray-300">{BUSINESS_HOURS.days}</p>
                <p className="mt-1 text-gray-700 dark:text-gray-300">{BUSINESS_HOURS.time}</p>
              </section>

              <section
                id="office"
                className="scroll-mt-24 border-t border-gray-200 dark:border-gray-700 pt-10"
              >
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  Office Address
                </h2>
                <p className="text-gray-700 dark:text-gray-300">
                  <span className="text-gray-500 dark:text-gray-400">Address: </span>
                  {OFFICE_ADDRESS}
                </p>
              </section>

              <section
                id="map"
                className="scroll-mt-24 border-t border-gray-200 dark:border-gray-700 pt-10"
              >
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  Find Us on Map
                </h2>
                <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700">
                  <iframe
                    title="TechElliptica office location"
                    src={GOOGLE_MAPS_EMBED_URL}
                    className="w-full h-72 sm:h-80"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
                <p className="mt-4">
                  <a
                    href={GOOGLE_MAPS_DIRECTIONS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 dark:text-primary-400 font-medium hover:underline"
                  >
                    Get directions on Google Maps
                  </a>
                </p>
              </section>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default SupportPage;
